import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Flex,
  Text,
  Spinner,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  VStack,
  HStack,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  InputRightElement,
  IconButton,
  SimpleGrid,
  Icon,
  Heading,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Grid,
  Image,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import {
  FiGrid,
  FiList,
  FiMapPin,
  FiHash,
  FiShoppingBag,
  FiTag,
  FiCheckCircle,
  FiImage,
  FiEye,
  FiPackage,
  FiExternalLink,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiEdit,
  FiRotateCw,
  FiDownload,
} from "react-icons/fi";
import { MdStore, MdStorefront } from "react-icons/md";
import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import { InfoCard, ImageCard, ProductTable } from "../SoftDrinks/cards";
import EditImageModal from "../SoftDrinks/EditImageModal";

/**
 * AvailabilityDp
 * Left panel: scrollable store list with search + pagination.
 * Right panel: Abstract View & Detail View tabs.
 * Detail View: full score-breakdown modal with presigned images (actual + annotated),
 * product summary, activation details, and cooler shelf breakdown — same as SoftDrinkDp.
 */
function AvailabilityDp({ fromDate, toDate }) {
  const userRole =
    localStorage.getItem("userRole") ||
    sessionStorage.getItem("userRole") ||
    "";
  const isOrgUser = userRole === "ORGIUSER";

  // ── Store list state ──────────────────────────────────────────────────────
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 100;

  // ── Selected store state ──────────────────────────────────────────────────
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreDetails, setSelectedStoreDetails] = useState(null);
  const [selectedStoreImages, setSelectedStoreImages] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // ── Score breakdown state ─────────────────────────────────────────────────
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const [productSummary, setProductSummary] = useState([]);
  const [shelfInfo, setShelfInfo] = useState([]);

  // ── Availability images ───────────────────────────────────────────────────
  const [modalActualImages, setModalActualImages] = useState([]);
  const [modalAnnotatedImages, setModalAnnotatedImages] = useState([]);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [actualImageUrl, setActualImageUrl] = useState("");
  const [annotatedImageUrl, setAnnotatedImageUrl] = useState("");

  // ── Cooler images ─────────────────────────────────────────────────────────
  const [modalCoolerActualImages, setModalCoolerActualImages] = useState([]);
  const [modalCoolerAnnotatedImages, setModalCoolerAnnotatedImages] = useState([]);
  const [modalCoolerImageIndex, setModalCoolerImageIndex] = useState(0);
  const [coolerActualImageUrl, setCoolerActualImageUrl] = useState("");
  const [coolerAnnotatedImageUrl, setCoolerAnnotatedImageUrl] = useState("");

  // ── Activation image ──────────────────────────────────────────────────────
  const [activationImageUrl, setActivationImageUrl] = useState("");
  const [isActivationImageOpen, setIsActivationImageOpen] = useState(false);
  const [loadingActivationImage, setLoadingActivationImage] = useState(false);

  // ── Store images carousel ─────────────────────────────────────────────────
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageRotation, setImageRotation] = useState(0);

  // ── Recalculate ───────────────────────────────────────────────────────────
  const [isRecalculating, setIsRecalculating] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();

  const toast = useToast();

  // ── Helpers ───────────────────────────────────────────────────────────────

  const getDateRangeBody = () => {
    const [fromYear, fromMonth, fromDay] = fromDate.split("-").map(Number);
    const [toYear, toMonth, toDay] = toDate.split("-").map(Number);
    return {
      from: { date: fromDay, month: fromMonth, year: fromYear },
      to: { date: toDay, month: toMonth, year: toYear },
    };
  };

  const generatePresignedUrls = async (s3Paths, month, year) => {
    const presignedPromises = s3Paths.map(async (s3Path) => {
      const presignedEndpoint = `https://s3service.sconexsoft.com/s3bucket/presigned-url?fileName=${encodeURIComponent(
        s3Path
      )}&bucketName=imageinsightimagesbeverages&operation=download&month=${month}&year=${year}&ResponseContentDisposition=attachment`;
      try {
        const response = await fetch(presignedEndpoint);
        if (!response.ok) throw new Error("Failed to fetch presigned URL");
        const presignedUrl = await response.text();
        return presignedUrl.trim();
      } catch (err) {
        console.error("Failed to generate presigned URL:", err);
        return null;
      }
    });
    return (await Promise.all(presignedPromises)).filter((url) => url);
  };

  const handleDownloadImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const response = await fetch(imageUrl, { mode: "cors", cache: "no-cache" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "image.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const link = document.createElement("a");
      link.href = imageUrl;
      link.download = "image.jpg";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // ── Image navigation ──────────────────────────────────────────────────────

  const handlePrevModalImage = () => {
    if (modalImageIndex > 0) {
      const newIndex = modalImageIndex - 1;
      setModalImageIndex(newIndex);
      setActualImageUrl(modalActualImages[newIndex] || "");
      setAnnotatedImageUrl(modalAnnotatedImages[newIndex] || "");
    }
  };

  const handleNextModalImage = () => {
    if (modalImageIndex < modalActualImages.length - 1) {
      const newIndex = modalImageIndex + 1;
      setModalImageIndex(newIndex);
      setActualImageUrl(modalActualImages[newIndex] || "");
      setAnnotatedImageUrl(modalAnnotatedImages[newIndex] || "");
    }
  };

  const handlePrevCoolerImage = () => {
    if (modalCoolerImageIndex > 0) {
      const newIndex = modalCoolerImageIndex - 1;
      setModalCoolerImageIndex(newIndex);
      setCoolerActualImageUrl(modalCoolerActualImages[newIndex] || "");
      setCoolerAnnotatedImageUrl(modalCoolerAnnotatedImages[newIndex] || "");
    }
  };

  const handleNextCoolerImage = () => {
    if (modalCoolerImageIndex < modalCoolerActualImages.length - 1) {
      const newIndex = modalCoolerImageIndex + 1;
      setModalCoolerImageIndex(newIndex);
      setCoolerActualImageUrl(modalCoolerActualImages[newIndex] || "");
      setCoolerAnnotatedImageUrl(modalCoolerAnnotatedImages[newIndex] || "");
    }
  };

  // ── Activation image ──────────────────────────────────────────────────────

  const handleViewActivation = async (detail) => {
    try {
      setLoadingActivationImage(true);
      const s3Path = detail?.s3ActualPath;
      if (!s3Path) return;
      const [toYear, toMonthNum] = toDate.split("-").map(Number);
      const urls = await generatePresignedUrls([s3Path], toMonthNum, toYear);
      setActivationImageUrl(urls[0] || "");
      setIsActivationImageOpen(true);
    } catch (err) {
      console.error("Failed to load activation image:", err);
    } finally {
      setLoadingActivationImage(false);
    }
  };

  // ── Fetch stores on date change ───────────────────────────────────────────

  useEffect(() => {
    if (!fromDate || !toDate) return;

    const fetchStores = async () => {
      setLoadingStores(true);
      setError("");
      try {
        const res = await apiClient.post(
          `${API_CONFIG.BASE_URL}/api/availability/stores`,
          getDateRangeBody()
        );
        const sorted = (res.data || []).sort((a, b) =>
          (a.storeName || "").toLowerCase().localeCompare((b.storeName || "").toLowerCase())
        );
        setStores(sorted);
        setFilteredStores(sorted);
        setCurrentPage(1);
        setSelectedStore(null);
        setSelectedStoreDetails(null);
        setScoreBreakdown(null);
      } catch (err) {
        console.error("Failed to load availability stores:", err);
        setError("Failed to load stores.");
        setStores([]);
        setFilteredStores([]);
      } finally {
        setLoadingStores(false);
      }
    };

    fetchStores();
  }, [fromDate, toDate]);

  // Keep filteredStores in sync when stores list updates
  useEffect(() => {
    if (searchQuery) {
      handleSearch(searchQuery);
    } else {
      setFilteredStores(stores);
    }
  }, [stores]);

  // ── Search ────────────────────────────────────────────────────────────────

  const handleSearch = (query) => {
    setSearchQuery(query);
    const filtered = stores
      .filter((s) => s.storeName?.toLowerCase().includes(query.toLowerCase()))
      .sort((a, b) =>
        (a.storeName || "").toLowerCase().localeCompare((b.storeName || "").toLowerCase())
      );
    setFilteredStores(filtered);
    setCurrentPage(1);
  };

  // ── Store click → fetch details + score breakdown + presigned images ───────

  const handleStoreClick = async (store) => {
    setSelectedStore(store);
    setActiveTab(0);
    setLoadingDetails(true);

    // Reset image state
    setActualImageUrl("");
    setAnnotatedImageUrl("");
    setCoolerActualImageUrl("");
    setCoolerAnnotatedImageUrl("");
    setModalActualImages([]);
    setModalAnnotatedImages([]);
    setModalCoolerActualImages([]);
    setModalCoolerAnnotatedImages([]);
    setModalImageIndex(0);
    setModalCoolerImageIndex(0);
    setCurrentImageIndex(0);
    setImageRotation(0);
    setProductSummary([]);
    setShelfInfo([]);
    setScoreBreakdown(null);

    try {
      const body = getDateRangeBody();
      const month = store.month;
      const year = store.year;

      // Fetch store details
      const detailRes = await apiClient.post(
        `${API_CONFIG.BASE_URL}/red-master/store-details?storeId=${store.storeId}`,
        body
      );
      const detailData = detailRes.data || null;
      setSelectedStoreDetails(detailData);

      // Fetch store images (imagePaths)
      if (detailData?.imagePaths && detailData.imagePaths.length > 0) {
        const urls = await generatePresignedUrls(detailData.imagePaths, month, year);
        setSelectedStoreImages(urls);
      } else {
        setSelectedStoreImages([]);
      }

      // Fetch score breakdown
      if (detailData?.storeId) {
        const breakdownRes = await apiClient.post(
          `${API_CONFIG.BASE_URL}/red-master/${detailData.storeId}/score-breakdown`,
          body
        );
        const breakdownData = breakdownRes.data || null;
        setScoreBreakdown(breakdownData);
        setProductSummary(breakdownData?.availability?.products || []);

        const actualPaths =
          breakdownData?.availability?.imagePathResponse?.s3pathActualFile || [];
        const annotatedPaths =
          breakdownData?.availability?.imagePathResponse?.s3pathAnnotatedFile || [];
        const coolerActualPaths =
          breakdownData?.cooler?.imagePathResponse?.s3pathActualFile || [];
        const coolerAnnotatedPaths =
          breakdownData?.cooler?.imagePathResponse?.s3pathAnnotatedFile || [];

        const shelves = actualPaths.map((_, index) => ({
          shelfNumber: index + 1,
          label: `Shelf ${index + 1}`,
        }));
        setShelfInfo(shelves);

        if (actualPaths.length > 0) {
          const actualUrls = await generatePresignedUrls(actualPaths, month, year);
          setModalActualImages(actualUrls);
          setActualImageUrl(actualUrls[0] || "");
        }
        if (annotatedPaths.length > 0) {
          const annotatedUrls = await generatePresignedUrls(annotatedPaths, month, year);
          setModalAnnotatedImages(annotatedUrls);
          setAnnotatedImageUrl(annotatedUrls[0] || "");
        }
        if (coolerActualPaths.length > 0) {
          const coolerActualUrls = await generatePresignedUrls(coolerActualPaths, month, year);
          setModalCoolerActualImages(coolerActualUrls);
          setCoolerActualImageUrl(coolerActualUrls[0] || "");
        }
        if (coolerAnnotatedPaths.length > 0) {
          const coolerAnnotatedUrls = await generatePresignedUrls(
            coolerAnnotatedPaths,
            month,
            year
          );
          setModalCoolerAnnotatedImages(coolerAnnotatedUrls);
          setCoolerAnnotatedImageUrl(coolerAnnotatedUrls[0] || "");
        }
      }
    } catch (err) {
      console.error("Failed to load store details:", err);
      toast({
        title: "Error",
        description: "Failed to load store details.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      setSelectedStoreDetails(null);
      setScoreBreakdown(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  // ── Edit save refresh ─────────────────────────────────────────────────────

  const handleEditSave = async () => {
    try {
      if (selectedStoreDetails) {
        handleStoreClick(selectedStore);
      }
    } catch (error) {
      console.error("Failed to refresh after edit:", error);
    }
  };

  // ── Recalculation ─────────────────────────────────────────────────────────

  const handleRecalculation = async () => {
    if (!selectedStore) {
      toast({
        title: "Select Store",
        description: "Please select a store first",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const runId = scoreBreakdown?.availability?.products?.[0]?.runId;
    const storeId = selectedStore.storeId || scoreBreakdown?.store?.storeId;
    if (!runId || !storeId) {
      toast({
        title: "Missing Data",
        description: "Store information is incomplete. Missing runId or storeId.",
        status: "error",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      return;
    }
    try {
      setIsRecalculating(true);
      const query = new URLSearchParams({ runId, storeId }).toString();
      const response = await fetch(
        `https://orgi-api.sconexsoft.com/aicalc/calculate-score?${query}`,
        { method: "GET" }
      );
      if (!response.ok) throw new Error("API failed");
      await response.text();
      toast({
        title: "Success",
        description: "Stores Calculated Successfully",
        status: "success",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
      handleStoreClick(selectedStore);
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Recalculation failed",
        status: "error",
        duration: 3000,
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  // ── Pagination ────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(filteredStores.length / pageSize);
  const paginatedStores = filteredStores.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Container maxW="container.2xl" px={0}>
        <Flex mt={2} gap={6} flexDir={{ base: "column", lg: "row" }}>

          {/* ── LEFT: Store List ─────────────────────────────────────────── */}
          <Box
            flex="1"
            bg="white"
            borderRadius="xl"
            overflow="hidden"
            boxShadow="sm"
            border="1px solid"
            borderColor="gray.200"
          >
            <Box p={4} borderBottom="1px solid" borderColor="gray.200">
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontSize="lg" fontWeight="bold" color="gray.800">
                  Stores
                  {!loadingStores && (
                    <Badge
                      ml={2}
                      colorScheme="red"
                      variant="subtle"
                      px={2}
                      py={1}
                      borderRadius="full"
                    >
                      {filteredStores.length}
                    </Badge>
                  )}
                </Text>
              </Flex>
              <InputGroup maxW="400px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search stores..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  bg="white"
                  borderColor="gray.300"
                  focusBorderColor="red.500"
                  _placeholder={{ color: "gray.400" }}
                />
                {searchQuery && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear search"
                      size="sm"
                      icon={<CloseIcon />}
                      onClick={() => {
                        setSearchQuery("");
                        setFilteredStores(stores);
                      }}
                      variant="ghost"
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            </Box>

            <Box p={0} height="500px" overflow="hidden">
              {loadingStores ? (
                <Flex justify="center" align="center" height="400px">
                  <Spinner size="lg" color="red.600" />
                </Flex>
              ) : error ? (
                <Center height="200px">
                  <Text color="red.500">{error}</Text>
                </Center>
              ) : (
                <Box height="100%">
                  <Box height="calc(100% - 60px)" overflow="auto">
                    <TableContainer>
                      <Table variant="simple" size="md">
                        <Tbody>
                          {paginatedStores.map((store, index) => (
                            <Tr
                              key={store.storeId || index}
                              role="group"
                              _hover={{ bg: "red.500", color: "white" }}
                              cursor="pointer"
                              onClick={() => handleStoreClick(store)}
                              bg={
                                selectedStore?.storeId === store.storeId
                                  ? "red.50"
                                  : "transparent"
                              }
                            >
                              <Td borderColor="gray.200">
                                <Text
                                  fontWeight="medium"
                                  color="gray.800"
                                  _groupHover={{ color: "white" }}
                                >
                                  {store.storeName || "-"}
                                </Text>
                              </Td>
                              <Td borderColor="gray.200">
                                <Button
                                  size="sm"
                                  color="red.500"
                                  bg="white"
                                  _groupHover={{ color: "white", bg: "red.600" }}
                                  _hover={{ transform: "scale(1.08)", boxShadow: "lg" }}
                                  transition="all 0.2s ease-in-out"
                                  colorScheme="red"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStoreClick(store);
                                  }}
                                >
                                  View
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  </Box>

                  {totalPages > 1 && (
                    <Flex
                      justify="center"
                      align="center"
                      gap={2}
                      p={3}
                      borderTop="1px solid"
                      borderColor="gray.200"
                    >
                      <Button
                        size="xs"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        isDisabled={currentPage === 1}
                        colorScheme="red"
                        variant="outline"
                      >
                        Prev
                      </Button>
                      <Text fontSize="sm" color="gray.600">
                        {currentPage} / {totalPages}
                      </Text>
                      <Button
                        size="xs"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        isDisabled={currentPage === totalPages}
                        colorScheme="red"
                        variant="outline"
                      >
                        Next
                      </Button>
                    </Flex>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* ── RIGHT: Abstract / Detail View Panel ──────────────────────── */}
          <Box
            flex="1"
            bg="white"
            borderRadius="xl"
            overflow="hidden"
            boxShadow="sm"
            border="1px solid"
            borderColor="gray.200"
            height="600px"
          >
            <Tabs
              index={activeTab}
              onChange={(index) => setActiveTab(index)}
              height="100%"
            >
              <TabList
                bg="red.100"
                p={2}
                borderRadius="lg"
                gap={2}
                mx={4}
                mt={4}
                display="flex"
                justifyContent="space-between"
              >
                <Tab
                  flex="1"
                  fontWeight="semibold"
                  fontSize="md"
                  py={3}
                  borderRadius="lg"
                  color="gray.600"
                  gap={2}
                  justifyContent="center"
                  display="flex"
                  alignItems="center"
                  _selected={{ bg: "red.600", color: "white", boxShadow: "md" }}
                  _hover={{ bg: "red.200", color: "red.700" }}
                >
                  <FiGrid size={16} /> Abstract View
                </Tab>
                <Tab
                  flex="1"
                  fontWeight="semibold"
                  fontSize="md"
                  py={3}
                  borderRadius="lg"
                  color="gray.600"
                  gap={2}
                  justifyContent="center"
                  display="flex"
                  alignItems="center"
                  _selected={{ bg: "red.600", color: "white", boxShadow: "md" }}
                  _hover={{ bg: "red.200", color: "red.700" }}
                >
                  <FiList size={16} /> Detail View
                </Tab>
              </TabList>

              <TabPanels height="calc(100% - 73px)" overflowY="auto">

                {/* ── Abstract View ── */}
                <TabPanel p={4} height="100%">
                  {loadingDetails ? (
                    <Center height="300px">
                      <Spinner size="lg" color="red.600" />
                    </Center>
                  ) : selectedStoreDetails ? (
                    <Box height="100%" overflowY="auto">
                      {/* Store Info + Scores */}
                      <Flex
                        direction={{ base: "column", md: "row" }}
                        gap={4}
                        align="stretch"
                        mb={6}
                      >
                        {/* Store Info Card */}
                        <Box
                          p={4}
                          bg="white"
                          borderRadius="xl"
                          border="1px solid"
                          borderColor="gray.200"
                          boxShadow="md"
                          flex="1"
                        >
                          <Heading size="sm" mb={4} color="gray.800" fontWeight="bold">
                            Store Information
                          </Heading>
                          <VStack align="start" spacing={3}>
                            <HStack align="start" spacing={3}>
                              <Icon as={FiShoppingBag} color="red.600" fontSize="20px" />
                              <Box>
                                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                  Store Name
                                </Text>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                  {selectedStoreDetails.storeName || "-"}
                                </Text>
                              </Box>
                            </HStack>
                            <HStack align="start" spacing={3}>
                              <Icon as={FiHash} color="yellow.600" fontSize="20px" />
                              <Box>
                                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                  Store ID
                                </Text>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                  {selectedStoreDetails.storeId || "-"}
                                </Text>
                              </Box>
                            </HStack>
                            <HStack align="start" spacing={3}>
                              <Icon as={FiTag} color="green.600" fontSize="20px" />
                              <Box>
                                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                  Store Type
                                </Text>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                  {selectedStoreDetails.storeType || "-"}
                                </Text>
                              </Box>
                            </HStack>
                            <HStack align="start" spacing={3}>
                              <Icon as={FiPackage} color="purple.600" fontSize="20px" />
                              <Box>
                                <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                  Store Class
                                </Text>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                  {selectedStoreDetails.storeClass || "-"}
                                </Text>
                              </Box>
                            </HStack>
                            {selectedStoreDetails.address && (
                              <HStack align="start" spacing={3}>
                                <Icon as={FiMapPin} color="orange.600" fontSize="20px" />
                                <Box>
                                  <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                    Address
                                  </Text>
                                  <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                    {selectedStoreDetails.address}
                                  </Text>
                                </Box>
                              </HStack>
                            )}
                          </VStack>
                        </Box>

                        {/* Store Scores Card */}
                        <Box
                          p={4}
                          bg="white"
                          borderRadius="xl"
                          border="1px solid"
                          borderColor="gray.200"
                          boxShadow="md"
                          flex="1"
                        >
                          <Heading size="sm" mb={4} color="gray.800" fontWeight="bold">
                            Store Scores
                          </Heading>
                          <SimpleGrid columns={1} spacing={3}>
                            <Box
                              textAlign="center"
                              p={3}
                              bg="green.50"
                              borderRadius="lg"
                              border="1px solid"
                              borderColor="green.200"
                            >
                              <Text fontSize="sm" color="green.700" fontWeight="medium">
                                Availability
                              </Text>
                              <Text fontSize="lg" fontWeight="bold" color="green.600">
                                {selectedStoreDetails?.availabilityScore || 0}
                              </Text>
                            </Box>
                            <Box
                              textAlign="center"
                              p={3}
                              bg="yellow.50"
                              borderRadius="lg"
                              border="1px solid"
                              borderColor="yellow.200"
                            >
                              <Text fontSize="sm" color="yellow.700" fontWeight="medium">
                                Activation
                              </Text>
                              <Text fontSize="lg" fontWeight="bold" color="yellow.600">
                                {selectedStoreDetails?.activationScore || 0}
                              </Text>
                            </Box>
                            <Box
                              textAlign="center"
                              p={3}
                              bg="red.50"
                              borderRadius="lg"
                              border="1px solid"
                              borderColor="red.200"
                            >
                              <Text fontSize="sm" color="red.700" fontWeight="medium">
                                Cooler
                              </Text>
                              <Text fontSize="lg" fontWeight="bold" color="red.600">
                                {selectedStoreDetails?.coolerScore || 0}
                              </Text>
                            </Box>
                            <Box
                              textAlign="center"
                              p={3}
                              bg="blue.50"
                              borderRadius="lg"
                              border="1px solid"
                              borderColor="blue.200"
                            >
                              <Text fontSize="sm" color="blue.600" fontWeight="medium">
                                Cooler Purity
                              </Text>
                              <Text fontSize="lg" fontWeight="bold" color="blue.600">
                                {selectedStoreDetails?.coolerPurity || 0}
                              </Text>
                            </Box>
                            <Box
                              textAlign="center"
                              p={3}
                              bg="purple.50"
                              borderRadius="lg"
                              border="1px solid"
                              borderColor="purple.200"
                            >
                              <Text fontSize="sm" color="purple.700" fontWeight="medium">
                                Cooler Bonus
                              </Text>
                              <Text fontSize="lg" fontWeight="bold" color="purple.600">
                                {selectedStoreDetails?.coolerBonus || 0}
                              </Text>
                            </Box>
                          </SimpleGrid>
                        </Box>
                      </Flex>

                      {/* Store Images */}
                      <Box>
                        <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={4}>
                          Store Images
                        </Text>
                        {selectedStoreImages.length > 0 ? (
                          <VStack spacing={4}>
                            <Box
                              position="relative"
                              w="full"
                              maxW="480px"
                              mx="auto"
                              borderRadius="xl"
                              overflow="hidden"
                              boxShadow="xl"
                              bg="gray.100"
                            >
                              <Image
                                src={selectedStoreImages[currentImageIndex]}
                                alt={`Store image ${currentImageIndex + 1}`}
                                objectFit="contain"
                                w="100%"
                                h="300px"
                                bg="white"
                                transform={`rotate(${imageRotation}deg)`}
                                transition="transform 0.3s ease"
                              />
                              <IconButton
                                aria-label="Rotate image"
                                icon={<Icon as={FiRotateCw} />}
                                position="absolute"
                                top="2"
                                right="10"
                                colorScheme="blue"
                                size="sm"
                                onClick={() =>
                                  setImageRotation((prev) => (prev + 90) % 360)
                                }
                                zIndex={2}
                              />
                              <IconButton
                                aria-label="Download image"
                                icon={<FiDownload />}
                                position="absolute"
                                top="2"
                                right="2"
                                colorScheme="red"
                                size="sm"
                                onClick={() =>
                                  handleDownloadImage(selectedStoreImages[currentImageIndex])
                                }
                                zIndex={2}
                              />
                              {selectedStoreImages.length > 1 && (
                                <>
                                  <Button
                                    position="absolute"
                                    left="4"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    onClick={() =>
                                      setCurrentImageIndex(Math.max(0, currentImageIndex - 1))
                                    }
                                    isDisabled={currentImageIndex === 0}
                                    size="sm"
                                    bg="whiteAlpha.900"
                                    boxShadow="md"
                                    _hover={{ bg: "white" }}
                                    zIndex={1}
                                  >
                                    <Icon as={FiChevronLeft} boxSize={5} />
                                  </Button>
                                  <Button
                                    position="absolute"
                                    right="4"
                                    top="50%"
                                    transform="translateY(-50%)"
                                    onClick={() =>
                                      setCurrentImageIndex(
                                        Math.min(
                                          selectedStoreImages.length - 1,
                                          currentImageIndex + 1
                                        )
                                      )
                                    }
                                    isDisabled={
                                      currentImageIndex === selectedStoreImages.length - 1
                                    }
                                    size="sm"
                                    bg="whiteAlpha.900"
                                    boxShadow="md"
                                    _hover={{ bg: "white" }}
                                    zIndex={1}
                                  >
                                    <Icon as={FiChevronRight} boxSize={5} />
                                  </Button>
                                </>
                              )}
                            </Box>
                            {selectedStoreImages.length > 1 && (
                              <HStack>
                                <Text fontSize="sm" color="gray.600" fontWeight="medium">
                                  {currentImageIndex + 1} / {selectedStoreImages.length}
                                </Text>
                              </HStack>
                            )}
                          </VStack>
                        ) : (
                          <Center
                            h="200px"
                            bg="gray.50"
                            borderRadius="xl"
                            border="2px dashed"
                            borderColor="gray.300"
                          >
                            <Text color="gray.500" fontWeight="medium">
                              No store images available
                            </Text>
                          </Center>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Center height="300px">
                      <VStack spacing={4}>
                        <Icon as={MdStore} boxSize={14} color="gray.300" />
                        <Text color="gray.500" fontWeight="medium">
                          Select a store to view details
                        </Text>
                      </VStack>
                    </Center>
                  )}
                </TabPanel>

                {/* ── Detail View ── */}
                <TabPanel p={4} height="100%">
                  <Box textAlign="center" mt={10}>
                    <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={4}>
                      Detail View
                    </Text>
                    <Text color="gray.600" mb={6}>
                      Click the button below to open detailed view in a popup
                    </Text>
                    <Button
                      colorScheme="red"
                      onClick={onOpen}
                      size="lg"
                      leftIcon={<FiExternalLink />}
                      isDisabled={!scoreBreakdown}
                    >
                      Open Detail View
                    </Button>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Flex>
      </Container>

      {/* ── Detail View Modal ─────────────────────────────────────────────── */}
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="6xl"
        isCentered
        motionPreset="slideInBottom"
        scrollBehavior="inside"
        blockScrollOnMount={false}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent
          maxH="90vh"
          bg="white"
          borderRadius="2xl"
          boxShadow="2xl"
          overflow="hidden"
        >
          <ModalHeader
            bgGradient="linear(to-r, red.600, red.500)"
            color="white"
            py={6}
            px={8}
            borderBottom="1px solid"
            borderColor="red.700"
          >
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={3}>
                <Icon as={MdStorefront} boxSize={7} />
                <Box>
                  <Heading size="md" fontWeight="bold">
                    Store Detail View
                  </Heading>
                  <Text fontSize="sm" color="red.100" mt={1}>
                    Comprehensive store analytics and visual insights
                  </Text>
                </Box>
              </Flex>
              <IconButton
                aria-label="Close modal"
                icon={<FiX />}
                variant="ghost"
                color="white"
                _hover={{ bg: "red.700" }}
                onClick={onClose}
                size="lg"
              />
            </Flex>
          </ModalHeader>

          <ModalBody px={8} py={6} overflowY="scroll">
            {scoreBreakdown ? (
              <VStack spacing={6} align="stretch">
                {/* Store Overview */}
                <Box
                  bg="white"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="lg"
                  p={6}
                  position="relative"
                  overflow="hidden"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "6px",
                    height: "100%",
                    bg: "red.600",
                  }}
                >
                  <Flex align="center" mb={4}>
                    <Icon as={MdStore} color="red.600" boxSize={6} mr={3} />
                    <Heading size="md" color="gray.800" fontWeight="semibold">
                      Store Overview
                    </Heading>
                  </Flex>
                  {(() => {
                    const store = scoreBreakdown.store || {};
                    return (
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                        <InfoCard icon={FiHash} label="Store ID" value={store.storeId || "-"} color="red" />
                        <InfoCard icon={FiTag} label="Store Code" value={store.storeId || "-"} color="green" />
                        <InfoCard icon={MdStore} label="Store Name" value={store.storeName || "-"} color="purple" />
                        <InfoCard
                          icon={FiPackage}
                          label="Store Type - Store Class"
                          value={`${store.storeType || "-"} - ${store.storeClass || "-"}`}
                          color="orange"
                        />
                        <InfoCard icon={FiPackage} label="Cooler Size" value={store.coolerSize || "-"} color="blue" />
                        <InfoCard
                          icon={FiMapPin}
                          label="Address"
                          value={store.storeAddress || "-"}
                          color="red"
                          colSpan={{ base: 1, md: 2, lg: 3 }}
                        />
                      </SimpleGrid>
                    );
                  })()}
                </Box>

                {/* Product Summary */}
                <Box
                  bg="white"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="lg"
                  p={6}
                >
                  <Flex align="center" justify="space-between" mb={4}>
                    <Flex align="center">
                      <Icon as={FiGrid} color="red.600" boxSize={6} mr={3} />
                      <Heading size="md" color="gray.800" fontWeight="semibold">
                        Product Summary
                      </Heading>
                    </Flex>
                    <HStack spacing={3}>
                      <Badge colorScheme="green" variant="subtle" px={3} py={1} fontSize="md">
                        availabilityScore:{" "}
                        {scoreBreakdown?.availability?.availabilityScore
                          ? `${scoreBreakdown.availability.availabilityScore} out of 70`
                          : "0 out of 70"}
                      </Badge>
                      <Badge colorScheme="red" variant="subtle" px={3} py={1} borderRadius="full">
                        {productSummary.length} items
                      </Badge>
                    </HStack>
                  </Flex>
                  {productSummary.length > 0 ? (
                    <Box borderRadius="lg" overflow="hidden" border="1px solid" borderColor="gray.200">
                      <ProductTable productSummary={productSummary} />
                    </Box>
                  ) : (
                    <Center h="200px">
                      <VStack spacing={3}>
                        <Icon as={FiPackage} boxSize={12} color="gray.400" />
                        <Text color="gray.600" fontWeight="medium">No products found</Text>
                      </VStack>
                    </Center>
                  )}
                </Box>

                {/* Visual Evidence */}
                <Box
                  bg="white"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="lg"
                  p={6}
                >
                  <Flex align="center" justify="space-between" mb={4}>
                    <Flex align="center">
                      <Icon as={FiImage} color="red.600" boxSize={6} mr={3} />
                      <Heading size="md" color="gray.800" fontWeight="semibold">
                        Visual Evidence
                      </Heading>
                    </Flex>
                    {shelfInfo.length > 0 && shelfInfo[modalImageIndex] && (
                      <Badge colorScheme="red" fontSize="md" px={4} py={2} borderRadius="full" fontWeight="bold">
                        {shelfInfo[modalImageIndex].label}
                      </Badge>
                    )}
                  </Flex>

                  <HStack spacing={6} mb={4} minH="300px">
                    <ImageCard
                      title="Actual Image"
                      subtitle="Original store photograph"
                      icon={FiEye}
                      imageUrl={actualImageUrl}
                      colorScheme="red"
                    />
                    {!isOrgUser && (
                      <ImageCard
                        title="Annotated Image"
                        subtitle="AI-processed analysis"
                        icon={FiCheckCircle}
                        imageUrl={annotatedImageUrl}
                        colorScheme="green"
                      />
                    )}
                  </HStack>

                  <HStack justify="space-between" mt={4} spacing={4}>
                    <Box width="200px" />
                    <HStack spacing={4}>
                      <IconButton
                        aria-label="Previous image"
                        icon={<FiChevronLeft />}
                        onClick={handlePrevModalImage}
                        isDisabled={modalImageIndex <= 0}
                        colorScheme="red"
                        variant="outline"
                        size="md"
                      />
                      <Text fontSize="md" color="gray.700" fontWeight="semibold" minW="80px" textAlign="center">
                        {modalActualImages.length > 0
                          ? `${modalImageIndex + 1} / ${modalActualImages.length}`
                          : "0 / 0"}
                      </Text>
                      <IconButton
                        aria-label="Next image"
                        icon={<FiChevronRight />}
                        onClick={handleNextModalImage}
                        isDisabled={modalImageIndex >= Math.max(0, modalActualImages.length - 1)}
                        colorScheme="red"
                        variant="outline"
                        size="md"
                      />
                    </HStack>
                    <Flex align="center" justify="space-between" gap={6}>
                      <Button
                        leftIcon={<FiEdit />}
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        onClick={onEditOpen}
                        _hover={{ bg: "red.50", transform: "translateY(-2px)", boxShadow: "md" }}
                        transition="all 0.2s"
                      >
                        Edit
                      </Button>
                      <EditImageModal
                        isOpen={isEditOpen}
                        onClose={onEditClose}
                        runId={scoreBreakdown?.availability?.products?.[0]?.runId}
                        storeId={scoreBreakdown?.availability?.products?.[0]?.storeId}
                        fileName={scoreBreakdown?.availability?.imagePathResponse?.s3pathActualFile?.[
                          modalImageIndex
                        ]
                          ?.split("/")
                          .pop()}
                        onSave={handleEditSave}
                      />
                      <Button
                        colorScheme="red"
                        variant="outline"
                        size="sm"
                        onClick={handleRecalculation}
                        isLoading={isRecalculating}
                        loadingText="Recalculating.."
                        _hover={{ bg: "red.50", transform: "translateY(-2px)", boxShadow: "md" }}
                        transition="all 0.2s"
                      >
                        ReCalculation
                      </Button>
                    </Flex>
                  </HStack>
                </Box>

                {/* Activation Details */}
                <Box
                  bg="white"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="lg"
                  p={6}
                >
                  <Flex align="center" justify="space-between" mb={4}>
                    <Flex align="center">
                      <Icon as={FiCheckCircle} color="yellow.600" boxSize={6} mr={3} />
                      <Heading size="md" color="gray.800" fontWeight="semibold">
                        Activation Details
                      </Heading>
                    </Flex>
                    <Badge colorScheme="blue" fontSize="md" variant="solid" px={4} py={2}>
                      activationScore:{" "}
                      {scoreBreakdown?.activation?.activationScore
                        ? `${scoreBreakdown.activation.activationScore} out of 10`
                        : "0 out of 10"}
                    </Badge>
                  </Flex>
                  {scoreBreakdown?.activation?.activationDetails?.length > 0 ? (
                    <TableContainer>
                      <Table variant="simple">
                        <Thead bg="yellow.50">
                          <Tr>
                            <Th color="yellow.700" fontWeight="bold">Company</Th>
                            <Th color="yellow.700" fontWeight="bold">Brand</Th>
                            <Th color="yellow.700" fontWeight="bold">Product</Th>
                            <Th color="yellow.700" fontWeight="bold">Action</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {scoreBreakdown.activation.activationDetails.map((detail, idx) => (
                            <Tr key={idx} bg={idx % 2 === 0 ? "yellow.50" : "transparent"}>
                              <Td fontWeight="medium">
                                {detail.product?.productCompany?.productCompanyName || "-"}
                              </Td>
                              <Td>{detail.product?.productBrand?.productBrandName || "-"}</Td>
                              <Td>{detail.product?.productName || "-"}</Td>
                              <Td>
                                <Button
                                  size="sm"
                                  colorScheme="yellow"
                                  onClick={() => handleViewActivation(detail)}
                                >
                                  View
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Center h="200px" color="gray.500">
                      No activation details available
                    </Center>
                  )}
                </Box>

                {/* Cooler Shelf Breakdown */}
                <Box
                  bg="white"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="lg"
                  p={6}
                >
                  <Flex align="center" justify="space-between" mb={4}>
                    <Flex align="center">
                      <Icon as={FiPackage} color="red.600" boxSize={6} mr={3} />
                      <Heading size="md" color="gray.800" fontWeight="semibold">
                        Cooler Shelf Breakdown
                      </Heading>
                    </Flex>
                    <Badge colorScheme="red" fontSize="lg" variant="solid" px={4} py={2}>
                      Total Cooler Purity Score:{" "}
                      {scoreBreakdown?.cooler?.coolerScore
                        ? `${scoreBreakdown.cooler.coolerScore} out of 20`
                        : "0 out of 20"}
                    </Badge>
                  </Flex>
                  {scoreBreakdown?.cooler?.shelfWiseCounts?.length > 0 ? (
                    <>
                      <TableContainer>
                        <Table variant="simple">
                          <Thead bg="red.50">
                            <Tr>
                              <Th color="red.700" fontWeight="bold" w="30%">Shelf Number</Th>
                              <Th color="red.700" fontWeight="bold" w="40%" textAlign="center">Product Percentage</Th>
                              <Th color="red.700" fontWeight="bold" w="30%">Status</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {scoreBreakdown.cooler.shelfWiseCounts.map((shelf, idx) => (
                              <Tr key={idx}>
                                <Td fontWeight="medium">{shelf.shelfNumber}</Td>
                                <Td textAlign="center" color="green.600" fontWeight="bold">
                                  {shelf.percentage}%
                                </Td>
                                <Td>
                                  <Badge
                                    colorScheme={shelf.result?.toUpperCase() === "PURE" ? "green" : "red"}
                                    variant="subtle"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    fontWeight="bold"
                                  >
                                    {shelf.result}
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>

                      {/* Cooler Images */}
                      <Box mt={6}>
                        <HStack spacing={6} flex={1}>
                          <ImageCard
                            title="Cooler Actual Image"
                            subtitle="Original cooler photograph"
                            icon={FiEye}
                            imageUrl={coolerActualImageUrl}
                            colorScheme="red"
                          />
                          {!isOrgUser && (
                            <ImageCard
                              title="Cooler Annotated Image"
                              subtitle="AI-processed analysis"
                              icon={FiCheckCircle}
                              imageUrl={coolerAnnotatedImageUrl}
                              colorScheme="green"
                            />
                          )}
                        </HStack>
                        <HStack justify="center" mt={4} spacing={4}>
                          <IconButton
                            aria-label="Previous cooler image"
                            icon={<FiChevronLeft />}
                            onClick={handlePrevCoolerImage}
                            isDisabled={modalCoolerImageIndex <= 0}
                          />
                          <Text fontSize="sm" color="gray.600">
                            {modalCoolerActualImages.length > 0
                              ? `${modalCoolerImageIndex + 1} / ${modalCoolerActualImages.length}`
                              : "0 / 0"}
                          </Text>
                          <IconButton
                            aria-label="Next cooler image"
                            icon={<FiChevronRight />}
                            onClick={handleNextCoolerImage}
                            isDisabled={
                              modalCoolerImageIndex >= Math.max(0, modalCoolerActualImages.length - 1)
                            }
                          />
                        </HStack>
                      </Box>
                    </>
                  ) : (
                    <Center h="200px" color="gray.500">
                      No cooler data available
                    </Center>
                  )}
                </Box>
              </VStack>
            ) : (
              <Center h="400px">
                <VStack spacing={4}>
                  <Icon as={MdStore} boxSize={16} color="gray.400" />
                  <Text fontSize="lg" color="gray.600" fontWeight="semibold">
                    No Store Data Available
                  </Text>
                  <Text color="gray.500" textAlign="center" maxW="md">
                    Select a store and open detail view to see comprehensive analytics
                  </Text>
                </VStack>
              </Center>
            )}
          </ModalBody>

          <ModalFooter px={8} py={4} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
            <Flex w="100%" justify="space-between" align="center">
              <Text fontSize="sm" color="gray.600">
                &copy; 2025 Image Insight. All rights reserved.
              </Text>
              <Button
                variant="outline"
                colorScheme="gray"
                leftIcon={<FiX />}
                onClick={onClose}
                size="lg"
                px={8}
                borderRadius="lg"
              >
                Close
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Activation Image Modal */}
      <Modal
        isOpen={isActivationImageOpen}
        onClose={() => setIsActivationImageOpen(false)}
        isCentered
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Activation Image</ModalHeader>
          <ModalBody>
            {loadingActivationImage ? (
              <Center py={8}>
                <Spinner />
              </Center>
            ) : activationImageUrl ? (
              <Box position="relative">
                <Image
                  src={activationImageUrl}
                  alt="Activation"
                  width="100%"
                  borderRadius="md"
                />
                <IconButton
                  aria-label="Download activation image"
                  icon={<FiDownload />}
                  position="absolute"
                  top="2"
                  right="2"
                  colorScheme="red"
                  size="sm"
                  onClick={() => handleDownloadImage(activationImageUrl)}
                />
              </Box>
            ) : (
              <Center py={8} color="gray.600">
                No image available
              </Center>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsActivationImageOpen(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default AvailabilityDp;